import { useState, useEffect, useRef } from 'react';
import api from '@/services/api';

export interface PriceChange {
  domain: string;
  changeLabel: string | null;
  changePercent: number | null;
}

interface PriceHistoryResponse {
  domain: string;
  points: unknown[];
  changeLabel: string | null;
  changePercent: number | null;
}

const BATCH_SIZE = 5;

/**
 * Fetches price change data for a list of domains once the scan is done.
 * Concurrency is capped at BATCH_SIZE (5) parallel requests per batch.
 * Errors are silently ignored — the domain just won't have a badge.
 *
 * @param domains - domains to check (typically only available ones)
 * @param enabled - set to true when the scan is complete
 * @returns Map<domain, PriceChange> — only contains entries with a non-null changeLabel
 */
export function usePriceChanges(domains: string[], enabled: boolean): Map<string, PriceChange> {
  const [priceChanges, setPriceChanges] = useState<Map<string, PriceChange>>(new Map());

  // Keep a ref in sync so the effect always reads the latest domains array
  // without needing to list it as an effect dep (avoids re-firing on every render).
  const domainsRef = useRef(domains);
  domainsRef.current = domains;

  useEffect(() => {
    if (!enabled) return;

    const currentDomains = domainsRef.current;
    if (currentDomains.length === 0) return;

    let cancelled = false;

    const fetchBatches = async () => {
      const result = new Map<string, PriceChange>();

      for (let i = 0; i < currentDomains.length; i += BATCH_SIZE) {
        if (cancelled) break;

        const batch = currentDomains.slice(i, i + BATCH_SIZE);

        const settled = await Promise.allSettled(
          batch.map((domain) =>
            api
              .get<PriceHistoryResponse>(
                `/api/domains/${encodeURIComponent(domain)}/price-history`
              )
              .then((res) => res.data)
          )
        );

        for (const item of settled) {
          if (item.status === 'fulfilled' && item.value.changeLabel !== null) {
            const { domain, changeLabel, changePercent } = item.value;
            result.set(domain, { domain, changeLabel, changePercent });
          }
        }
      }

      if (!cancelled) {
        setPriceChanges(result);
      }
    };

    fetchBatches();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return priceChanges;
}
